import { EntrancePlantDetailDTO, EntrancePlantStructureCreateDTO, IPeBuildingConfig, PeInterfaceConfig, IPeRiserConfig, RiserInterfaceMode, BuildingCreateDTO, PlantConfigStep, RFAConfigDTO, RiserDTO, RiserConfigDTO, FloorDTO, FloorConfigDTO } from '@/../client-generator/generated-clients/plants';
import { Vue, Options } from 'vue-class-component';
import plantsClient from '@/core/services/plants';
import { SET_SELECTED_PLANT_ID } from '@/core/store/action-types';
import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';
import { getInterfaceModeLabel, isUniqueLinkingActive } from '@/core/utils/EntrancePlant/entrance-plant.utils';

interface RiserWithInterfaces extends IPeRiserConfig {
  interfaces: PeInterfaceConfig[];
  isBackbone?: boolean;
}

interface BuildingConfig extends IPeBuildingConfig {
  linkedRisersConfig: RiserWithInterfaces[];
  linkedRisersIds: string[];
}

interface FloorConfig {
  id: string;
  name: string;
  apartmentDistribution: ApartmentDistribution[];
}

interface ApartmentDistribution {
  interfaceId: string;
  count: number;
}

interface RiserFloorConfig {
  riserId: string;
  riserName: string;
  floorsCount: number;
  floors: FloorConfig[];
}

@Options({
  name: 'DialogEntrancePlantCreateSetupBuildingBRFA',
  props: {
    plant: {
      required: true,
      type: Object as () => EntrancePlantDetailDTO,
    },
    structureCreate: {
      required: true,
      type: Object as () => EntrancePlantStructureCreateDTO,
    },
    plantId: {
      type: Number,
      required: false,
    },   
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  components: {NumberButtons},
  emits: ['is-loading', 'onBack', 'onNext', 'plant-edited'],
})
export default class DialogEntrancePlantCreateSetupBuildingBRFA extends Vue {
  plant!: EntrancePlantDetailDTO;
  plantId!: number;
  structureCreate!: EntrancePlantStructureCreateDTO;

  currentBuildingIndex: number = 0;
  backboneAndRisersWithInterfaces: RiserWithInterfaces[] = [];
  buildingConfigs: BuildingConfig[] = [];
  riserFloorConfigs: Map<string, RiserFloorConfig> = new Map();
  uniqueLinkingMode: boolean = false;

  getInterfaceModeLabel = getInterfaceModeLabel;


  get pageTitle(): string {
    return `${this.$t('AppResourceEP.SetupBuildingWizard')} ${this.currentBuildingIndex + 1}`;
  }

  get totalBuildings(): number {
    return this.buildingConfigs.length;
  }

  get currentBuilding(): BuildingConfig | undefined {
    return this.buildingConfigs[this.currentBuildingIndex];
  }

  get isLastBuilding(): boolean {
    return this.currentBuildingIndex >= this.totalBuildings - 1;
  }

  get nextButtonLabel(): string {
    return this.$t('AppResource.Continue') as string;
  }

  get hasBackboneApartments(): boolean {
    return this.backboneApartmentsCount > 0;
  }

  get backboneApartmentsCount(): number {
    return this.structureCreate.pePlantConfig?.numbersOfBackboneApartments ?? 0;
  }

  get availableRiserOptions(): { riserId: string; riserName: string, isBackbone: boolean, interfacesComboMode: string }[] {
    return this.backboneAndRisersWithInterfaces.map(riser => {
      let interfacesComboMode = '';
      if (riser.interfaces.length == 1) {
        interfacesComboMode = this.$t('AppResourceEP.ShortComboMode5IndependentRiser');
      } else if (riser.interfaces.length == 2) {
        interfacesComboMode = this.$t('AppResourceEP.ShortComboMode6ExtendedRiserAndOneMode7ExtendedRiserExpansion');
      } else if (riser.interfaces.length > 2) {
        interfacesComboMode = this.$t('AppResourceEP.ShortComboMode6ExtendedRiserAndTwoMode7ExtendedRiserExpansion');
      }
      return {
        riserId: riser.id || '',
        riserName: riser.name || '',
        isBackbone: riser.isBackbone ?? false,
        interfacesComboMode: riser.isBackbone ? '' : interfacesComboMode,
      };
    });
  }

  get isNextDisabled(): boolean {
    const building = this.currentBuilding;
    if (!building) return true;
    
    // Check if all linked risers have floors configured and all apartments are assigned
    return building.linkedRisersConfig.some(riser => {
      const floorConfig = this.riserFloorConfigs.get(riser.id!);
      if (!floorConfig || floorConfig.floorsCount === 0) return true;
      
      // Check if all apartments are assigned for each interface
      return riser.interfaces.some(intf => {
        const totalApartments = intf.numberOfApartments || 0;
        const assignedApartments = floorConfig.floors.reduce((sum, floor) => {
          const distribution = floor.apartmentDistribution.find(d => d.interfaceId === intf.id);
          return sum + (distribution?.count || 0);
        }, 0);
        return assignedApartments !== totalApartments;
      });
    });
  }

  mounted(): void {
    this.initializeRiserWithInterfaces();
    this.initializeBuildingConfigs();
    this.checkUniqueLinkingMode();
  }

  checkUniqueLinkingMode(): void {
    const pePlantConfig = this.structureCreate.pePlantConfig?.pePlantConfig;
    if (pePlantConfig) {
      this.uniqueLinkingMode = isUniqueLinkingActive(pePlantConfig, this.hasBackboneApartments);
    }
  }

  initializeRiserWithInterfaces(): void {
    const interfaces = this.structureCreate.pePlantConfig?.pePlantConfig?.interfaces || [];
    this.backboneAndRisersWithInterfaces = (this.structureCreate.pePlantConfig?.pePlantConfig?.risers || []).map(riser => {
      const linkedInterfaces = interfaces.filter(intf => intf.peRiserConfigId === riser.id);
      return {
        ...riser,
        interfaces: linkedInterfaces,
      };
    });

    if (this.hasBackboneApartments) {
      const backboneInterface = interfaces.find(intf => intf.isBackboneInterface);
      if (backboneInterface) {
        this.backboneAndRisersWithInterfaces.unshift({
          index: -1,
          id: backboneInterface.peRiserConfigId,
          name: backboneInterface.name,
          numberOfApartments: backboneInterface.numberOfApartments,
          interfaces: [backboneInterface],
          isBackbone: true,
        });
      }
    }
  }

  initializeBuildingConfigs(): void {
    if (this.structureCreate.pePlantConfig?.pePlantConfig?.buildings && this.structureCreate.pePlantConfig.pePlantConfig.buildings.length > 0) {
      const interfaces = this.structureCreate.pePlantConfig?.pePlantConfig?.interfaces || [];
      const backboneInterface = interfaces.find(intf => intf.isBackboneInterface);
      
      this.buildingConfigs = this.structureCreate.pePlantConfig.pePlantConfig.buildings.map(building => {
        const linkedRisersIds = this.structureCreate.pePlantConfig?.pePlantConfig?.risers?.filter(riser => {
          return building.id === riser.peBuildingConfigId;
        }).map(riser => riser.id!) || [];
        
        if (backboneInterface && backboneInterface.peBuildingConfigId === building.id) {
          linkedRisersIds.unshift(backboneInterface.peRiserConfigId!);
        }
        
        const linkedRisersConfig = this.backboneAndRisersWithInterfaces.filter(riser => linkedRisersIds.includes(riser.id!));
        
        // Initialize floor configs for this building's risers
        linkedRisersConfig.forEach(riser => {
          if (!this.riserFloorConfigs.has(riser.id!)) {
            this.riserFloorConfigs.set(riser.id!, {
              riserId: riser.id!,
              riserName: riser.name!,
              floorsCount: 0,
              floors: [],
            });
            this.addFloor(riser.id!);
          }
        });

        return {
          ...building,
          linkedRisersConfig: linkedRisersConfig,
          linkedRisersIds: linkedRisersIds,
        } as BuildingConfig;
      });
    }
    this.currentBuildingIndex = 0;
  }

  onLinkedRisersChange(buildingIndex: number, riserIds: string[]): void {
    const building = this.buildingConfigs[buildingIndex];
    if (!building) return;

    building.linkedRisersIds = [...riserIds];

    // Sync linked risers configs based on ids
    const linkedConfigs = this.backboneAndRisersWithInterfaces.filter(r => r.id && riserIds.includes(r.id));
    linkedConfigs.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    building.linkedRisersConfig = linkedConfigs;

    const allowed = new Set(riserIds);

    // Remove floor configs for unlinked risers
    Array.from(this.riserFloorConfigs.keys()).forEach(riserId => {
      if (!allowed.has(riserId)) {
        this.riserFloorConfigs.delete(riserId);
      }
    });

    // Ensure floor configs exist for linked risers and at least one floor
    building.linkedRisersConfig.forEach(riser => {
      if (riser.id && !this.riserFloorConfigs.has(riser.id)) {
        this.riserFloorConfigs.set(riser.id, {
          riserId: riser.id,
          riserName: riser.name || '',
          floorsCount: 0,
          floors: [],
        });
        this.addFloor(riser.id!);
      }
    });
  }

  isRiserDisabledForBuilding(buildingIndex: number, riserId: string): boolean {
    // Prevent linking the same riser to multiple buildings
    return this.buildingConfigs.some((building, index) =>
      index !== buildingIndex && building.linkedRisersConfig.some(riser => riser.id === riserId)
    );
  }
  
  addFloor(riserId: string): void {
    const riserConfig = this.riserFloorConfigs.get(riserId);
    if (!riserConfig) return;

    const newFloorIndex = riserConfig.floors.length + 1;
    const newFloor: FloorConfig = {
      id: `${riserId}_floor_${Date.now()}`,
      name: `${this.$t('AppResourceEP.Floor')} ${toLabel(newFloorIndex, this.structureCreate.pePlantConfig!.pePlantConfig!.denominationFloors)}`,
      apartmentDistribution: [],
    };

    riserConfig.floors.push(newFloor);
    riserConfig.floorsCount = riserConfig.floors.length;
    
    // Distribute apartments equally across floors
    this.updateApartmentDistribution(riserId);
  }

  removeFloor(riserId: string, floorIndex: number): void {
    const riserConfig = this.riserFloorConfigs.get(riserId);
    if (!riserConfig) return;

    riserConfig.floors.splice(floorIndex, 1);
    riserConfig.floorsCount = riserConfig.floors.length;
    
    // Redistribute apartments
    this.updateApartmentDistribution(riserId);
  }

  private updateApartmentDistribution(riserId: string): void {
    const riserConfig = this.riserFloorConfigs.get(riserId);
    if (!riserConfig || riserConfig.floors.length === 0) return;

    const riser = this.backboneAndRisersWithInterfaces.find(r => r.id === riserId);
    if (!riser) return;

    const floorsCount = riserConfig.floors.length;
    
    riserConfig.floors.forEach(floor => {
      floor.apartmentDistribution = riser.interfaces.map(intf => {
        const totalApartments = intf.numberOfApartments || 0;
        const distribution = this.distributeElementsInGroups(totalApartments, floorsCount);
        const floorIndex = riserConfig.floors.indexOf(floor);
        
        return {
          interfaceId: intf.id!,
          count: distribution[floorIndex],
        };
      });
    });
  }

  private distributeElementsInGroups(totalElements: number, groupsCount: number): number[] {
    if (groupsCount === 0) return [];
    
    const baseCount = Math.floor(totalElements / groupsCount);
    const remainder = totalElements % groupsCount;
    
    const distribution: number[] = [];
    for (let i = 0; i < groupsCount; i++) {
      distribution.push(baseCount + (i < remainder ? 1 : 0));
    }
    
    return distribution;
  }

  apartmentCountValues(riser: RiserWithInterfaces, interfaceId: string, floorId: string): { label: number; value: number; disabled: boolean }[] {
    const selectedInterface = riser.interfaces.find(i => i.id === interfaceId);
    if (!selectedInterface) return [];

    const totalApartmentsForInterface = selectedInterface.numberOfApartments || 0;
    const riserId = riser.id!;
    const riserConfig = this.riserFloorConfigs.get(riserId);
    if (!riserConfig) return [];

    // Find current floor and its index
    const currentFloorIndex = riserConfig.floors.findIndex(f => f.id === floorId);
    if (currentFloorIndex === -1) return [];

    // Calculate already allocated apartments for this interface across OTHER floors
    let allocatedInOtherFloors = 0;
    riserConfig.floors.forEach((floor, idx) => {
      if (idx !== currentFloorIndex) {
        const distribution = floor.apartmentDistribution.find(d => d.interfaceId === interfaceId);
        if (distribution) {
          allocatedInOtherFloors += distribution.count;
        }
      }
    });

    // Max available for this floor = total - allocated in other floors
    const maxAvailableForThisFloor = totalApartmentsForInterface - allocatedInOtherFloors;
    return [...Array(totalApartmentsForInterface + 1).keys()].map(i => ({ 
      label: i, 
      value: i,
      disabled: i > maxAvailableForThisFloor
    }));
  }

  changeApartmentDistributionCount(distribution: ApartmentDistribution, e: {originalEvent: any, value: number}): void {
    const isDisabled = (e.originalEvent.currentTarget.classList as DOMTokenList).contains('p-disabled');
    if (isDisabled) return;
    
    distribution.count = e.value;
  }

  buildingName(index?: number): string {
    if (!index) return '';
    return `${this.$t('AppResourceEP.Name')} ${this.$t(
      'AppResourceEP.Building'
    )} ${toLabel(index, this.structureCreate.pePlantConfig!.pePlantConfig!.denominationBuildings)}`;
  }

  floorName(index?: number): string {
    if (!index) return '';
    return `${this.$t('AppResourceEP.Name')} ${this.$t(
      'AppResourceEP.Floor'
    )} ${toLabel(index, this.structureCreate.pePlantConfig!.pePlantConfig!.denominationFloors)}`;
  }

  handleBackButton(): void {
    if (this.currentBuildingIndex > 0) {
      this.currentBuildingIndex--;
    } else {
      this.$emit('onBack', 'DialogEntrancePlantCreateSetupAddBuildings');
    }
  }

  async handleNextButton() {
    this.$emit('is-loading', true);
    
    try {
      await this.updatePlantStructure(this.getDataToSave());

      if (this.isLastBuilding) {
        this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.END_CONFIG;
        this.structureCreate.riserFlow = true;
        await this.updatePlantStructure(this.structureCreate);
        this.$emit('onNext', 'DialogEntrancePlantCreateBRFACodeExample');
      } else {
        this.currentBuildingIndex++;
      }
    } finally {
      this.$emit('is-loading', false);
    }
  }

  getDataToSave(): EntrancePlantStructureCreateDTO {
    this.buildingConfigs.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    const currentBuilding = this.buildingConfigs[this.currentBuildingIndex];
    
    const buildingConfigsToSave: BuildingCreateDTO[] = [
      new BuildingCreateDTO({
        buildingId: currentBuilding.id,
        name: currentBuilding.name,
        apartmentsCount: currentBuilding.linkedRisersConfig.reduce((sum, riser) => sum + (riser.numberOfApartments || 0), 0),
        linkedRisersIds: currentBuilding.linkedRisersIds,
      })
    ];

    const currentRFAConfig: { [key: string]: RFAConfigDTO; } = {}
    currentRFAConfig[currentBuilding.id!] = new RFAConfigDTO({
      risers: [],
      numberOfApartments: buildingConfigsToSave[0].apartmentsCount,
      floorsWithoutRiser: [],
    });

    //i need to create the RFaconfig to give the raiser theirl linked floors and apartments
    currentBuilding.linkedRisersConfig.forEach(riser => {
      const riserFloorConfig = this.riserFloorConfigs.get(riser.id!);
      if (riserFloorConfig) {
        const floorsDTO = riserFloorConfig.floors.map(floor => {
          return new FloorConfigDTO({
            index: riserFloorConfig.floors.indexOf(floor),
            name: floor.name,
            numberOfApartments: floor.apartmentDistribution.reduce((sum, dist) => sum + dist.count, 0),
            linkedInterfaces: floor.apartmentDistribution.map(dist => {
              return new PeInterfaceConfig({
                id: dist.interfaceId,
                numberOfApartments: dist.count,
                peRiserConfigId: riser.id,
              });
            }),
          });
        });
        const riserDTO = new RiserConfigDTO({
          riserId: riser.id,
          index: riser.index,
          name: riser.name,
          floors: floorsDTO,
        });
        currentRFAConfig[currentBuilding.id!].risers!.push(riserDTO);
      }
    });

    this.structureCreate.pePlantConfig!.currentStep = PlantConfigStep.BUILDINGS_RISERS_LINK;
    return new EntrancePlantStructureCreateDTO({
      plantConfigStructure: currentRFAConfig,
      buildingsCreate: buildingConfigsToSave,
      pePlantConfig: this.structureCreate.pePlantConfig,
      riserFlow: true,
    });
  }

  private async updatePlantStructure(structure: EntrancePlantStructureCreateDTO): Promise<void> {
    if (!this.plant) return;

    try {
      const result = await plantsClient.structure(this.plant.desktopId!, structure);
      this.structureCreate.pePlantConfig = result.pePlantConfig;
      this.structureCreate.buildingsCreate = [...(this.structureCreate.buildingsCreate || []), ...(result.buildingsCreate || [])];
      this.structureCreate.plantConfigStructure = {
        ...this.structureCreate.plantConfigStructure,
        ...result.plantConfigStructure,
      };
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 6000,
      });
    }
  }
}
